/**
 * Median.co shell detection and native navigation via median:// protocol.
 * @see https://docs.median.co/docs/detecting-app-usage
 * @see https://docs.median.co/docs/javascript-bridge#median-protocol
 */

/**
 * True when the page runs inside the Median (or legacy GoNative) app WebView.
 * Default Median UA includes "median"; legacy builds may still send "gonative".
 */
export function isMedianShell () {
    if (typeof navigator === 'undefined' || !navigator.userAgent) {
        return false
    }
    const ua = navigator.userAgent
    return ua.indexOf('median') > -1 || ua.indexOf('gonative') > -1
}

/**
 * Historically the bottom tab JSON was applied only on iOS, not Android
 * (GoNativeAndroid / gonativeand). Median Android UA includes "MedianAndroid".
 */
export function shouldApplyMedianBottomTabBar () {
    if (typeof navigator === 'undefined' || !navigator.userAgent) {
        return false
    }
    const ua = navigator.userAgent
    if (ua.indexOf('MedianAndroid') > -1) {
        return false
    }
    if (ua.indexOf('gonativeand') > -1) {
        return false
    }
    return true
}

/**
 * Shape payloads like median.sidebar.setItems({ items, enabled, persist }).
 * @see https://docs.median.co/docs/dynamic-menu-items
 */
export function normalizeSidebarSetItemsPayload (data) {
    if (data == null) {
        return null
    }
    if (Array.isArray(data)) {
        return { items: data, enabled: true, persist: true }
    }
    if (typeof data === 'object' && Array.isArray(data.items)) {
        return {
            ...data,
            enabled: data.enabled !== false,
            persist: data.persist !== false
        }
    }
    return { items: data, enabled: true, persist: true }
}

/**
 * Shape payloads like median.tabNavigation.setTabs({ enabled, items }).
 * @see https://docs.median.co/docs/dynamic-tab-menu
 */
export function normalizeTabBarSetTabsPayload (data) {
    if (data == null) {
        return null
    }
    if (Array.isArray(data)) {
        return { enabled: true, items: data }
    }
    if (typeof data === 'object' && Array.isArray(data.items)) {
        return {
            ...data,
            enabled: data.enabled !== false
        }
    }
    return { enabled: true, items: data }
}

/**
 * Fire one or more Median protocol commands. Multiple commands use nativebridge/multi
 * so they are not dropped (sequential window.location assignments can lose earlier ones).
 *
 * @param {Object} opts
 * @param {Object|null} [opts.registrationCustomData] - median://registration/send
 * @param {*} [opts.sidebarItems] - menu array or { items, enabled?, persist? } for sidebar setItems
 * @param {*} [opts.tabsPayload] - tabs array or { items, enabled? } for tabs setTabs
 */
export function applyMedianNativeNavigationCommands ({ registrationCustomData, sidebarItems, tabsPayload }) {
    const urls = []
    if (registrationCustomData != null) {
        urls.push('median://registration/send?customData=' + encodeURIComponent(JSON.stringify(registrationCustomData)))
    }
    if (sidebarItems != null) {
        const sidebarPayload = normalizeSidebarSetItemsPayload(sidebarItems)
        urls.push('median://sidebar/setItems?items=' + encodeURIComponent(JSON.stringify(sidebarPayload)))
    }
    if (tabsPayload != null) {
        const tabsWrapped = normalizeTabBarSetTabsPayload(tabsPayload)
        urls.push('median://tabs/setTabs?tabs=' + encodeURIComponent(JSON.stringify(tabsWrapped)))
    }
    if (urls.length === 0) {
        return
    }
    if (urls.length === 1) {
        window.location.href = urls[0]
        return
    }
    window.location.href = 'median://nativebridge/multi?urls=' + encodeURIComponent(JSON.stringify(urls))
}



// WEBPACK FOOTER //
// ./src/helpers/medianNativeNavigation.js